<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import TemplatePreviewBubble from './TemplatePreviewBubble.vue';

const props = defineProps({
  template: {
    type: Object,
    required: true,
  },
  inboxId: {
    type: [Number, String],
    required: true,
  },
  wabaId: {
    type: String,
    default: '',
  },
  selectedDateRange: {
    type: String,
    default: 'LAST_7_DAYS',
  },
});

const emit = defineEmits(['back', 'edit', 'delete', 'updateDateRange']);

const { t } = useI18n();
const store = useStore();

const isLoading = ref(false);
const analyticsData = ref(null);
const activeDateRange = ref(props.selectedDateRange);

const DATE_RANGES = [
  { key: 'LAST_7_DAYS', days: 7, label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_7_DAYS' },
  { key: 'LAST_14_DAYS', days: 14, label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_14_DAYS' },
  { key: 'LAST_30_DAYS', days: 30, label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_30_DAYS' },
  { key: 'LAST_90_DAYS', days: 90, label: 'INBOX_MGMT.WHATSAPP_TEMPLATES.DATE_RANGE.LAST_90_DAYS' },
];

const fetchAnalytics = async () => {
  isLoading.value = true;
  try {
    const rangeObj = DATE_RANGES.find(r => r.key === activeDateRange.value) || DATE_RANGES[0];
    const now = Math.floor(Date.now() / 1000);
    const start = now - rangeObj.days * 86400;

    const data = await store.dispatch(
      'inboxes/fetchWhatsAppSingleTemplateAnalytics',
      {
        inboxId: props.inboxId,
        templateId: props.template.id,
        params: {
          start_time: start,
          end_time: now,
          granularity: 'DAILY',
        },
      }
    );

    analyticsData.value = data;
  } catch (error) {
    analyticsData.value = {
      analytics_by_template: {},
      summary: { sent: 0, delivered: 0, read: 0, clicked: 0, spent: null, cost_per_delivered: null },
    };
  } finally {
    isLoading.value = false;
  }
};

watch(activeDateRange, newRange => {
  emit('updateDateRange', newRange);
  fetchAnalytics();
});

onMounted(fetchAnalytics);

const currentMetrics = computed(() => {
  const tplId = (props.template.id || '').toString();
  const byTpl = analyticsData.value?.analytics_by_template?.[tplId];
  if (byTpl) return byTpl;

  return (
    analyticsData.value?.summary || {
      sent: 0,
      delivered: 0,
      read: 0,
      clicked: 0,
      spent: null,
      cost_per_delivered: null,
    }
  );
});

const hasDataPoints = computed(() => {
  const tplId = (props.template.id || '').toString();
  const byTpl = analyticsData.value?.analytics_by_template?.[tplId];
  const points = byTpl?.data_points || [];
  return points.length > 0 && currentMetrics.value.sent > 0;
});

const chartCollection = computed(() => {
  const tplId = (props.template.id || '').toString();
  const byTpl = analyticsData.value?.analytics_by_template?.[tplId];
  const points = byTpl?.data_points || [];

  const labels = points.map(p => {
    const date = new Date(p.start * 1000);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  });

  return {
    labels,
    datasets: [
      {
        label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.SENT'),
        backgroundColor: '#00a5f4',
        data: points.map(p => p.sent || 0),
        borderRadius: 4,
      },
      {
        label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.DELIVERED'),
        backgroundColor: '#10b981',
        data: points.map(p => p.delivered || 0),
        borderRadius: 4,
      },
      {
        label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.READ'),
        backgroundColor: '#6366f1',
        data: points.map(p => p.read || 0),
        borderRadius: 4,
      },
    ],
  };
});

const deliveryRate = computed(() => {
  if (!currentMetrics.value.sent || currentMetrics.value.sent === 0) return 0;
  return Math.round(
    ((currentMetrics.value.delivered || 0) / currentMetrics.value.sent) * 100
  );
});

const openRate = computed(() => {
  if (!currentMetrics.value.delivered || currentMetrics.value.delivered === 0) return 0;
  return Math.round(
    ((currentMetrics.value.read || 0) / currentMetrics.value.delivered) * 100
  );
});

const metaTemplateUrl = computed(() => {
  return `https://business.facebook.com/wa/manage/message-templates/?waba_id=${props.wabaId}&templates_tab=category_updates`;
});

const canEdit = computed(() => {
  const status = props.template.status?.toUpperCase();
  return ['APPROVED', 'REJECTED', 'PAUSED'].includes(status);
});

const getStatusBadge = computed(() => {
  const status = props.template.status?.toUpperCase() || 'PENDING';
  if (status === 'APPROVED') {
    return {
      label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.APPROVED'),
      bg: 'bg-n-teal-3 text-n-teal-11',
    };
  }
  if (status === 'PENDING') {
    return {
      label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.PENDING'),
      bg: 'bg-n-amber-3 text-n-amber-11',
    };
  }
  if (status === 'REJECTED') {
    return {
      label: t('INBOX_MGMT.WHATSAPP_TEMPLATES.STATUS.REJECTED'),
      bg: 'bg-n-ruby-3 text-n-ruby-10',
    };
  }
  return {
    label: status,
    bg: 'bg-n-alpha-2 text-n-slate-11',
  };
});
</script>

<template>
  <div class="flex flex-col gap-6 w-full max-w-6xl pb-10">
    <!-- Top Navigation & Details Header -->
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex items-center gap-3">
        <NextButton
          xs
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.BACK_BUTTON')"
          icon="i-lucide-arrow-left"
          @click="$emit('back')"
        />
        <div class="h-4 w-px bg-n-weak" />
        <div class="flex items-center gap-2 flex-wrap">
          <h3 class="text-lg font-bold text-n-slate-12">
            {{ template.name }}
          </h3>
          <span
            class="px-2 py-0.5 text-xs font-semibold rounded-full"
            :class="getStatusBadge.bg"
          >
            {{ getStatusBadge.label }}
          </span>
          <span
            class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ $t(`INBOX_MGMT.WHATSAPP_TEMPLATES.CATEGORY.${template.category?.toUpperCase() || 'UTILITY'}`) }}
          </span>
          <span
            class="px-2 py-0.5 text-xs font-semibold rounded-full bg-n-alpha-2 text-n-slate-11"
          >
            {{ template.language }}
          </span>
        </div>
      </div>

      <!-- Header Actions -->
      <div class="flex items-center gap-2">
        <!-- Date range selector -->
        <div class="relative flex items-center h-8">
          <span
            class="i-lucide-calendar absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none z-10 block"
          />
          <select
            v-model="activeDateRange"
            class="!h-8 !m-0 !py-0 !pl-8 pr-7 [background-image:none] !bg-none appearance-none text-xs bg-n-alpha-1 hover:bg-n-alpha-2 text-n-slate-12 rounded-xl border border-n-weak focus:outline-none focus:ring-1 focus:ring-n-brand cursor-pointer font-medium !leading-8"
          >
            <option
              v-for="range in DATE_RANGES"
              :key="range.key"
              :value="range.key"
            >
              {{ $t(range.label) }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down absolute right-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-10 pointer-events-none block"
          />
        </div>

        <NextButton
          v-if="canEdit"
          sm
          ghost
          slate
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.FORM.EDIT_TITLE')"
          icon="i-lucide-pencil"
          @click="$emit('edit', template)"
        />

        <NextButton
          sm
          ghost
          ruby
          :label="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE_CONFIRM.TITLE')"
          icon="i-lucide-trash-2"
          @click="$emit('delete', template)"
        />

        <a
          v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.META_LINK.TOOLTIP')"
          :href="metaTemplateUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center justify-center size-8 rounded-xl text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-blue-11 transition-colors border border-n-weak"
        >
          <span class="i-lucide-external-link text-sm" />
        </a>
      </div>
    </div>

    <!-- Main 2-Column Section -->
    <div class="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
      <!-- Left Column: Tu plantilla -->
      <div class="lg:col-span-5 flex flex-col gap-3">
        <div
          class="flex flex-col rounded-2xl border border-n-weak bg-white dark:bg-n-solid-2 shadow-sm overflow-hidden"
        >
          <div class="px-5 py-4 border-b border-n-weak flex items-center justify-between">
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.TITLE') }}
            </h4>
          </div>
          <div class="p-4">
            <TemplatePreviewBubble :template="template" />
          </div>
        </div>
      </div>

      <!-- Right Column: Performance Summary -->
      <div class="lg:col-span-7 flex flex-col gap-4">
        <!-- Top 2 Metrics Cards -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <!-- Importe gastado -->
          <div
            class="flex flex-col gap-2 p-5 rounded-2xl bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.SPENT.TITLE') }}
              </span>
              <span
                v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.SPENT.TOOLTIP')"
                class="i-lucide-info text-xs text-n-slate-10 hover:text-n-slate-12 cursor-help"
              />
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ currentMetrics.spent !== null ? `$${currentMetrics.spent.toFixed(2)}` : '--' }}
            </div>
            <span class="text-xs text-n-slate-10">--</span>
          </div>

          <!-- Costo por mensaje entregado -->
          <div
            class="flex flex-col gap-2 p-5 rounded-2xl bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm"
          >
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-n-slate-11">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.COST_PER_DELIVERED.TITLE') }}
              </span>
              <span
                v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.COST_PER_DELIVERED.TOOLTIP')"
                class="i-lucide-info text-xs text-n-slate-10 hover:text-n-slate-12 cursor-help"
              />
            </div>
            <div class="text-2xl font-bold text-n-slate-12 mt-1">
              {{ currentMetrics.cost_per_delivered !== null ? `$${currentMetrics.cost_per_delivered.toFixed(4)}` : '--' }}
            </div>
            <span class="text-xs text-n-slate-10">--</span>
          </div>
        </div>

        <!-- Main Performance Card -->
        <div
          class="flex flex-col rounded-2xl bg-white dark:bg-n-solid-2 border border-n-weak shadow-sm overflow-hidden"
        >
          <!-- Card Header -->
          <div
            class="px-5 py-4 border-b border-n-weak flex items-center justify-between"
          >
            <div class="flex items-center gap-1.5">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.TITLE') }}
              </h4>
              <span
                v-tooltip="$t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.TOOLTIP')"
                class="i-lucide-info text-xs text-n-slate-10 hover:text-n-slate-12 cursor-help"
              />
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="flex items-center justify-center py-24">
            <Spinner size="" />
          </div>

          <!-- Active Metrics Content -->
          <div v-else-if="hasDataPoints" class="p-5 flex flex-col gap-5">
            <!-- Key Metric Chips -->
            <div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div class="p-3 rounded-xl bg-n-alpha-1 border border-n-weak flex flex-col">
                <span class="text-[11px] text-n-slate-10">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.SENT') }}
                </span>
                <span class="text-lg font-bold text-n-slate-12">
                  {{ currentMetrics.sent.toLocaleString() }}
                </span>
              </div>

              <div class="p-3 rounded-xl bg-n-alpha-1 border border-n-weak flex flex-col">
                <span class="text-[11px] text-n-slate-10">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.DELIVERED') }}
                </span>
                <span class="text-lg font-bold text-n-teal-11">
                  {{ currentMetrics.delivered.toLocaleString() }}
                </span>
              </div>

              <div class="p-3 rounded-xl bg-n-alpha-1 border border-n-weak flex flex-col">
                <span class="text-[11px] text-n-slate-10">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.READ') }}
                </span>
                <span class="text-lg font-bold text-n-blue-11">
                  {{ currentMetrics.read.toLocaleString() }}
                </span>
              </div>

              <div class="p-3 rounded-xl bg-n-alpha-1 border border-n-weak flex flex-col">
                <span class="text-[11px] text-n-slate-10">
                  {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.DELIVERY_RATE') }}
                </span>
                <span class="text-lg font-bold text-n-slate-12">
                  {{ deliveryRate }}%
                </span>
              </div>
            </div>

            <!-- Chart -->
            <div class="h-64 w-full pt-2">
              <BarChart :collection="chartCollection" />
            </div>
          </div>

          <!-- Empty State (Matching Image 2) -->
          <div
            v-else
            class="flex flex-col items-center justify-center py-20 px-6 gap-3 text-center"
          >
            <div
              class="w-14 h-14 rounded-2xl bg-n-alpha-1 border border-n-weak flex items-center justify-center mb-1 text-n-slate-10"
            >
              <span class="i-lucide-line-chart text-2xl" />
            </div>
            <h5 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.EMPTY_TITLE') }}
            </h5>
            <p class="text-xs text-n-slate-10 max-w-sm">
              {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.PERFORMANCE.EMPTY_DESCRIPTION') }}
            </p>
          </div>
        </div>

        <!-- Meta Terms Disclaimer Footer -->
        <p class="text-[11px] text-n-slate-10 mt-1">
          {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.META_DISCLAIMER') }}
          <a
            href="https://www.facebook.com/legal/technology_terms"
            target="_blank"
            rel="noopener noreferrer"
            class="text-n-blue-11 hover:underline font-medium"
          >
            {{ $t('INBOX_MGMT.WHATSAPP_TEMPLATES.DETAIL.META_CONDITIONS_LINK') }}
          </a>
        </p>
      </div>
    </div>
  </div>
</template>
